import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';

/**
 * Generic Tailwind hover-expand miller-columns picker (no AntD). Hover a node to open its children in the
 * next column; click any level to select it (value = the path of ids). Used for the Territory picker and
 * the Staff (role → team) picker on the coverage + staff pages.
 */
export interface CascOpt { value: string; label: string; children?: CascOpt[]; badge?: string }

export function useClickOutside<T extends HTMLElement>(ref: RefObject<T | null>, onOut: () => void) {
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onOut(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onOut]);
}

export const cascControl = (open: boolean) =>
  `flex h-9 items-center gap-2 rounded-control border bg-surface px-3 text-sm outline-none transition ${
    open ? 'border-brand-500 ring-2 ring-brand-500/25' : 'border-line hover:border-line-strong'
  }`;

function labelsForPath(opts: CascOpt[], path?: string[]): string[] {
  const out: string[] = [];
  let cur = opts;
  for (const id of path ?? []) {
    const node = cur.find((o) => o.value === id);
    if (!node) break;
    out.push(node.label);
    cur = node.children ?? [];
  }
  return out;
}

const badgeChip = (b: string) =>
  b === 'SH' ? 'bg-sky-500/15 text-sky-600' : 'bg-brand-500/15 text-brand-700 dark:text-brand-300';

export function HoverCascader({ options, value, onChange, placeholder, minWidth = 220 }: {
  options: CascOpt[]; value?: string[]; onChange: (v?: string[]) => void; placeholder: string; minWidth?: number;
}) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<string[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  const columns = useMemo(() => {
    const cols: CascOpt[][] = [options];
    let cur = options;
    for (const id of hover) {
      const node = cur.find((o) => o.value === id);
      if (node?.children?.length) { cols.push(node.children); cur = node.children; } else break;
    }
    return cols;
  }, [options, hover]);

  const labels = labelsForPath(options, value);
  const pick = (colIdx: number, o: CascOpt) => { onChange([...hover.slice(0, colIdx), o.value]); setOpen(false); };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => (open ? setOpen(false) : (setHover(value ?? []), setOpen(true)))}
        className={cascControl(open)} style={{ minWidth }}>
        <span className={`flex-1 truncate text-left ${labels.length ? 'text-content' : 'text-subtle'}`}>
          {labels.length ? labels.join(' › ') : placeholder}
        </span>
        {value?.length ? (
          <span role="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); onChange(undefined); }}
            className="text-subtle hover:text-content"><X size={14} /></span>
        ) : null}
        <ChevronDown size={15} className={`shrink-0 text-subtle transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 flex overflow-hidden rounded-card border border-line bg-surface shadow-pop animate-pop-in">
          {columns.map((col, ci) => (
            <ul key={ci} className="max-h-72 min-w-[180px] overflow-y-auto border-r border-line py-1 last:border-r-0">
              {col.map((o) => {
                const onTrail = hover[ci] === o.value;
                const selected = value?.length === ci + 1 && value[ci] === o.value;
                return (
                  <li key={o.value}>
                    <button type="button"
                      onMouseEnter={() => setHover([...hover.slice(0, ci), o.value])}
                      onClick={() => pick(ci, o)}
                      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition ${
                        onTrail ? 'bg-brand-50 text-brand-800 dark:bg-brand-900/30 dark:text-brand-200' : 'text-content hover:bg-surface-2'
                      } ${selected ? 'font-semibold' : ''}`}>
                      <span className="flex-1 truncate">{o.label}</span>
                      {o.badge ? <span className={`rounded-pill px-1.5 py-0.5 text-[11px] font-semibold ${badgeChip(o.badge)}`}>{o.badge}</span> : null}
                      {o.children?.length ? <ChevronRight size={14} className="shrink-0 text-subtle" /> : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ))}
        </div>
      )}
    </div>
  );
}
